import { useEffect } from 'react';
import './MagicalTorqueAnimation.css';

/**
 * PROFESSIONAL MAGICAL TORQUE ANIMATION
 * Fairy-type physical move - revs up magical energy and crashes into opponent
 * 
 * Key Features:
 * - Fairy-type physical attack (100 base power, 100% accuracy)
 * - 30% chance to confuse
 * - Starmobile signature move with fairy energy charge
 * - Professional gaming quality with sparkling fairy impact
 */
export default function MagicalTorqueAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Fairy Energy Rev-Up (0-500ms)
    playerEl.classList.add('magical-torque-user-charge');

    const fairyAura = document.createElement('div');
    fairyAura.className = 'magical-torque-fairy-aura';
    fairyAura.style.left = `${playerX - 70}px`;
    fairyAura.style.top = `${playerY - 70}px`;
    container.appendChild(fairyAura);
    elements.push(fairyAura);

    // Fairy sparkles swirling
    for (let i = 0; i < 12; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'magical-torque-sparkle';
      const angle = (i / 12) * 360;
      const radius = 55 + (i % 3) * 10;
      sparkle.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 4}px`;
      sparkle.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 4}px`;
      sparkle.style.animationDelay = `${i * 35}ms`;
      container.appendChild(sparkle);
      elements.push(sparkle);
    }

    // Spinning fairy ring
    const spinRing = document.createElement('div');
    spinRing.className = 'magical-torque-spin-ring';
    spinRing.style.left = `${playerX - 45}px`;
    spinRing.style.top = `${playerY - 45}px`;
    container.appendChild(spinRing);
    elements.push(spinRing);

    // PHASE 2: Fairy Charge (500-850ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('magical-torque-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Fairy trail
      for (let i = 0; i < 12; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'magical-torque-trail';
          const progress = i / 12;
          trail.style.left = `${playerX + dx * progress * 0.85 + (Math.random() - 0.5) * 20 - 5}px`;
          trail.style.top = `${playerY + dy * progress * 0.85 + (Math.random() - 0.5) * 20 - 5}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 25);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Magical Impact (850-1350ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('magical-torque-opponent-hit');
      playerEl.classList.remove('magical-torque-user-dash');

      // Fairy explosion
      const explosion = document.createElement('div');
      explosion.className = 'magical-torque-explosion';
      explosion.style.left = `${opponentX - 100}px`;
      explosion.style.top = `${opponentY - 100}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Fairy star burst
      for (let i = 0; i < 20; i++) {
        const burstAngle = (i / 20) * 360;
        const burstDist = 55 + Math.random() * 45;
        const burst = document.createElement('div');
        burst.className = 'magical-torque-star-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 12}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      // Fairy shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'magical-torque-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 90);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Confusion sparkles
      for (let i = 0; i < 6; i++) {
        const confuse = document.createElement('div');
        confuse.className = 'magical-torque-confuse-star';
        confuse.style.left = `${opponentX + (Math.random() - 0.5) * 80 - 6}px`;
        confuse.style.top = `${opponentY - 30 + (Math.random() - 0.5) * 20 - 6}px`;
        confuse.style.animationDelay = `${200 + i * 50}ms`;
        container.appendChild(confuse);
        elements.push(confuse);
      }
    }, 850);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (1600ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('magical-torque-user-charge', 'magical-torque-user-dash');
      opponentEl.classList.remove('magical-torque-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1600);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('magical-torque-user-charge', 'magical-torque-user-dash');
      opponentEl.classList.remove('magical-torque-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

MagicalTorqueAnimation.moveType = 'fairy';
